import React, { useState, useEffect } from "react";
import { FaWhatsapp, FaArrowRight, FaCheckCircle } from "react-icons/fa";
import { SITE_CONTENT } from "../content";

const HeroSection = () => {
  const { headline, description, primaryButton, secondaryButton, slides, badges } =
    SITE_CONTENT.pages.homePage.heroSection;

  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5500);
    return () => clearInterval(timer);
  }, [slides.length]);

  const scrollToBooking = (e) => {
    e.preventDefault();
    const el = document.getElementById("booking-section");
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section className="relative min-h-screen min-h-[100dvh] flex items-center justify-center overflow-hidden pt-20 pb-12 sm:pt-24 sm:pb-16 lg:py-28">
      {/* Background Slideshow with Smooth Crossfade */}
      <div className="absolute inset-0 bg-[#07160c]">
        {slides.map((slide, idx) => (
          <div
            key={idx}
            className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
              currentSlide === idx ? "opacity-100 scale-100" : "opacity-0 scale-105"
            } transform transition-transform duration-[7000ms]`}
          >
            <img
              src={slide.image}
              alt={slide.alt}
              className="w-full h-full object-cover object-center filter brightness-[0.75] contrast-[1.05]"
            />
          </div>
        ))}

        {/* Ambient Overlay - visible feast background with high contrast for text */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#051109]/90 via-[#051109]/60 to-[#051109]/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#051109] via-transparent to-[#051109]/40" />
        <div className="absolute bottom-0 left-0 right-0 h-28 bg-gradient-to-t from-[#051109] to-transparent pointer-events-none" />
      </div>

      {/* Decorative Glow Orbs */}
      <div className="absolute top-1/4 left-10 w-80 h-80 bg-[#22c55e]/15 rounded-full blur-[130px] pointer-events-none" />
      <div className="absolute bottom-1/4 right-10 w-96 h-96 bg-[#E8A040]/15 rounded-full blur-[140px] pointer-events-none" />

      {/* Main Container */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 w-full">
        <div className="max-w-2xl mx-auto sm:mx-0 flex flex-col items-center sm:items-start text-center sm:text-left">
          
          {/* Halal & Outdoor Catering Pill */}
          <div className="inline-flex flex-wrap items-center justify-center sm:justify-start gap-2 mb-4">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#14532d]/90 border border-[#22c55e]/40 text-[#22c55e] text-[11px] font-semibold tracking-wider uppercase shadow-[0_0_20px_rgba(34,197,94,0.25)]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#22c55e] animate-ping" />
              100% Halal Certified
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-black/40 backdrop-blur-sm border border-white/20 text-[#f5ecd8] text-[11px] font-medium tracking-wide">
              <FaCheckCircle className="text-[#E8A040] w-3 h-3" />
              Outdoor Catering Specialists
            </span>
          </div>

          {/* Exact Docx H1 Headline - Centered on Mobile */}
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-[40px] font-light text-white leading-[1.24] tracking-tight font-serif mb-4 text-center sm:text-left">
            {headline}
          </h1>

          {/* Exact Docx Description - Centered and Clamped to 3 Lines on Mobile */}
          <p className="hero-desc-clamp line-clamp-3 sm:line-clamp-none text-[#d8ded6] text-sm sm:text-base leading-relaxed mb-8 font-light max-w-xl text-center sm:text-left mx-auto sm:mx-0">
            {description}
          </p>

          {/* Action Buttons - Single Row on Mobile & Desktop */}
          <div className="flex flex-row items-center justify-center sm:justify-start gap-2 sm:gap-4 mb-8 w-full max-w-md sm:max-w-none">
            <a
              href="#booking-section"
              onClick={scrollToBooking}
              className="flex-1 sm:flex-initial inline-flex items-center justify-center gap-1.5 sm:gap-2.5 px-3 sm:px-7 py-2.5 sm:py-3.5 bg-gradient-to-r from-[#D4AF37] via-[#E8A040] to-[#F5C070] text-[#0a1a0f] font-bold text-[10px] xs:text-[11px] sm:text-xs tracking-normal sm:tracking-[0.14em] uppercase rounded-full shadow-[0_0_25px_rgba(232,160,64,0.35)] hover:shadow-[0_0_40px_rgba(232,160,64,0.55)] hover:scale-105 transition-all duration-300 group text-center shrink-0 min-w-0 whitespace-nowrap"
            >
              <span className="hidden sm:inline">{primaryButton.label}</span>
              <span className="sm:hidden">Request Quote</span>
              <FaArrowRight className="w-2.5 h-2.5 sm:w-3 sm:h-3 transition-transform duration-300 group-hover:translate-x-1 shrink-0" />
            </a>

            <a
              href={secondaryButton.whatsappUrl}
              target="_blank"
              rel="noreferrer"
              className="flex-1 sm:flex-initial inline-flex items-center justify-center gap-1.5 sm:gap-2.5 px-3 sm:px-6 py-2.5 sm:py-3.5 bg-[#0d2a17]/90 hover:bg-[#143e23] border border-[#22c55e]/50 text-white text-[10px] xs:text-[11px] sm:text-xs font-semibold tracking-normal sm:tracking-wide rounded-full transition-all duration-300 hover:scale-105 shadow-lg group text-center shrink-0 min-w-0 whitespace-nowrap"
            >
              <FaWhatsapp className="text-[#25D366] w-3.5 h-3.5 sm:w-4 sm:h-4 transition-transform group-hover:scale-110 shrink-0" />
              <span className="hidden md:inline">{secondaryButton.label}</span>
              <span className="md:hidden">Call / WhatsApp</span>
            </a>
          </div>

          {/* Trust Badges & Highlights Grid */}
          <div className="pt-6 border-t border-white/15 grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 w-full">
            {badges.map((b, i) => (
              <div key={i} className="flex items-center justify-center sm:justify-start gap-2 text-center sm:text-left">
                <span className="w-1.5 h-1.5 rounded-full bg-[#E8A040] shrink-0" />
                <span className="text-white/85 text-[11px] sm:text-xs font-medium leading-tight">
                  {b}
                </span>
              </div>
            ))}
          </div>

        </div>
      </div>

      {/* Slide Indicators */}
      <div className="absolute bottom-6 right-6 lg:right-12 z-20 flex items-center gap-2">
        {slides.map((_, idx) => (
          <button
            key={idx}
            onClick={() => setCurrentSlide(idx)}
            aria-label={`Go to slide ${idx + 1}`}
            className={`transition-all duration-300 rounded-full ${
              currentSlide === idx ? "w-8 h-2 bg-[#E8A040]" : "w-2 h-2 bg-white/40 hover:bg-white/70"
            }`}
          />
        ))}
      </div>
    </section>
  );
};

export default HeroSection;
